import { ConnectionQuality, DisconnectReason, Room, RoomEvent, Track } from 'livekit-client';
import type { LocalVideoTrack, Participant, RemoteTrack } from 'livekit-client';

import { translate } from '@/shared/i18n/instance';
import { ApiError } from '@/shared/services/api/api-error';
import { api } from '@/shared/services/api/endpoints';
import type { Call, CallKind } from '@/shared/services/api/types';

import { isCallBusy, useCallStore } from '@/stores/call.store';
import type { CallEndReason, CallQuality } from '@/stores/call.store';
import { useSessionStore } from '@/stores/session.store';

import { clearCallAlerts } from './call-alerts';
import { attachRemoteAudio, routeToSpeaker, startCallAudio, stopCallAudio } from './call-audio';
import { callRoomOptions, ENDED_SCREEN_MS, REMOTE_RETURN_GRACE_MS } from './call-options';
import { startCallService, stopCallService } from './foreground-service';
import { ringer } from './ringer';

/**
 * Le moteur d'un appel sur cet appareil : un seul à la fois.
 *
 * Le serveur décide (qui sonne, qui entre, quand c'est fini) et le dit par le
 * canal temps réel ; LiveKit transporte le son et l'image. Ce module tient les
 * deux bouts : il appelle l'API pour chaque geste, applique chaque état que le
 * serveur annonce, et tient la pièce LiveKit, que l'écran lit sans la posséder.
 * L'écran peut ainsi se fermer (on revient à la discussion) sans que l'appel
 * s'arrête.
 *
 * Le même fichier sert le site : seuls changent, par plateforme, la sortie du
 * son (`call-audio`), les sonneries (`ringer`), le service de premier plan et
 * les alertes du système (`foreground-service`, `call-alerts`).
 *
 * L'appelant entre dans la pièce dès qu'il appelle, sans publier : quand
 * l'autre décroche, la connexion est déjà faite, et la voix passe à la
 * seconde, même sur un réseau lent.
 */

let room: Room | null = null;
const roomListeners = new Set<() => void>();
/** Compteur de changements de la pièce : l'instantané que lisent les écrans. */
let roomVersion = 0;
let detachers: Array<() => void> = [];
let ringTimer: ReturnType<typeof setTimeout> | null = null;
let returnTimer: ReturnType<typeof setTimeout> | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;
/** L'image coupée parce que l'application est passée en arrière-plan. */
let cameraPausedByBackground = false;

const store = () => useCallStore.getState();
const myId = () => useSessionStore.getState().me?.id ?? null;

/** Où j'en suis dans cet appel, selon le serveur. */
function myState(call: Call) {
  return call.participants.find((person) => person.id === myId())?.state ?? null;
}

// --------------------------------------------------------------- lecture --

/** La pièce de l'appel en cours, pour l'écran qui en montre les images. */
export function getCallRoom(): Room | null {
  return room;
}

/** Le numéro du dernier changement de la pièce (`useSyncExternalStore`). */
export function getCallRoomVersion(): number {
  return roomVersion;
}

/** Prévenu à chaque changement de la pièce : arrivée, départ, image, parole. */
export function onCallRoomChange(listener: () => void): () => void {
  roomListeners.add(listener);
  return () => {
    roomListeners.delete(listener);
  };
}

function notifyRoom(): void {
  roomVersion += 1;
  for (const listener of roomListeners) {
    listener();
  }
}

// ---------------------------------------------------------------- gestes --

/** Appeler depuis une conversation. Rend l'appel créé, ou `null` en cas d'échec. */
export async function startCall(conversationId: string, kind: CallKind): Promise<Call | null> {
  if (isCallBusy(store())) {
    return store().call;
  }
  clearTimers();
  store().reset();
  const video = kind === 'VIDEO';
  store().patch({ phase: 'outgoing', cameraOn: video, speakerOn: video });

  try {
    const call = await api.calls.start(conversationId, kind);
    store().patch({ call });
    if (call.status === 'ENDED') {
      // Tout le monde était déjà en ligne ailleurs : occupé.
      finish(call.outcome ?? 'FAILED');
      return call;
    }
    if (call.status === 'RINGING' && call.callerId === myId()) {
      ringer.ringback();
      armRingTimeout(call);
    } else {
      // L'autre appelait au même instant, ou le groupe parlait déjà : on rejoint.
      store().patch({ phase: 'connecting' });
    }
    await connect(call, store().phase === 'connecting');
    return call;
  } catch (error) {
    fail(error);
    return null;
  }
}

/** Décrocher l'appel qui sonne ici (ou revenir dans celui qu'on a perdu). */
export async function answerCall(): Promise<void> {
  const call = store().call;
  if (!call || store().phase !== 'incoming') {
    return;
  }
  clearTimers();
  ringer.stop();
  clearCallAlerts(call.id);
  const video = call.kind === 'VIDEO';
  store().patch({ phase: 'connecting', cameraOn: video, speakerOn: video });
  try {
    const joined = await api.calls.join(call.id);
    store().patch({ call: joined });
    await connect(joined, true);
  } catch (error) {
    fail(error);
  }
}

/**
 * Rejoindre un appel qui vit déjà dans une conversation (« Appel en cours ·
 * Rejoindre » d'un groupe), sans attendre qu'il sonne.
 */
export async function joinCall(call: Call): Promise<void> {
  if (isCallBusy(store())) {
    return;
  }
  adopt(call);
  await answerCall();
}

/**
 * Raccrocher, refuser ou annuler : le serveur départage selon où l'on en est.
 * L'écran se ferme sans attendre sa réponse ; s'il ne répond pas, il constatera
 * seul l'absence (sonnerie expirée, présence perdue).
 */
export function hangUp(): Promise<void> {
  const phase = store().phase;
  return leave(phase === 'incoming' ? 'DECLINED' : phase === 'outgoing' ? 'CANCELLED' : 'ANSWERED');
}

export async function setMicrophone(on: boolean): Promise<void> {
  store().patch({ micOn: on });
  await room?.localParticipant.setMicrophoneEnabled(on).catch(() => undefined);
  notifyRoom();
}

export async function setCamera(on: boolean): Promise<void> {
  store().patch({ cameraOn: on });
  await room?.localParticipant
    .setCameraEnabled(on, { facingMode: store().facing })
    .catch(() => undefined);
  notifyRoom();
}

export async function setSpeaker(on: boolean): Promise<void> {
  store().patch({ speakerOn: on });
  await routeToSpeaker(on).catch(() => undefined);
}

/** Caméra de face ↔ de dos, sans quitter la pièce ni couper l'image chez l'autre. */
export async function flipCamera(): Promise<void> {
  const facing = store().facing === 'user' ? 'environment' : 'user';
  store().patch({ facing });
  const track = room?.localParticipant.getTrackPublication(Track.Source.Camera)?.track as
    LocalVideoTrack | undefined;
  await track?.restartTrack({ facingMode: facing }).catch(() => undefined);
  notifyRoom();
}

/**
 * L'application passe en arrière-plan : l'image s'arrête (personne ne filme
 * sa poche), la voix continue. Elle reprend au retour, si on l'avait laissée.
 */
export function setBackground(background: boolean): void {
  const local = room?.localParticipant;
  if (!local || store().call?.kind !== 'VIDEO' || !store().cameraOn) {
    return;
  }
  if (background && !cameraPausedByBackground) {
    cameraPausedByBackground = true;
    void local.setCameraEnabled(false).catch(() => undefined);
  } else if (!background && cameraPausedByBackground) {
    cameraPausedByBackground = false;
    void local.setCameraEnabled(true, { facingMode: store().facing }).catch(() => undefined);
  }
}

// ------------------------------------------------- ce que dit le serveur --

/**
 * Un appel tel que le serveur l'annonce (`call:incoming`, `call:changed`, ou
 * relu au retour au premier plan). Tout changement d'état passe par ici.
 */
export function applyServerCall(next: Call): void {
  const { call: current, phase } = store();
  const mine = myState(next);

  if (!current || current.id !== next.id) {
    // Un autre appel que celui en cours : il ne compte que s'il sonne chez moi
    // et que je suis libre (sinon, le serveur me compte déjà occupé).
    const reachable = mine === 'RINGING' || (mine === 'JOINED' && !room);
    if (next.status !== 'ENDED' && reachable && !isCallBusy(store())) {
      adopt(next);
    }
    return;
  }

  store().patch({ call: next });
  if (next.status === 'ENDED') {
    if (phase !== 'ended') {
      finish(next.outcome ?? 'ANSWERED');
    }
    return;
  }
  if (phase === 'incoming' && mine !== 'RINGING' && !(mine === 'JOINED' && !room)) {
    // Décroché ailleurs : sur un autre de mes appareils, ou par un collègue.
    finish(mine === 'DECLINED' ? 'DECLINED' : 'ANSWERED_ELSEWHERE');
    return;
  }
  if (phase === 'outgoing' && next.status === 'ONGOING') {
    clearRingTimer();
    ringer.stop();
    store().patch({ phase: 'connecting' });
    void publishMedia();
  }
}

/**
 * L'écran d'un appel ouvert par son lien (une notification touchée,
 * l'application lancée par elle) : on relit l'appel et on l'adopte s'il sonne
 * encore ; fini, l'écran le dit et se referme.
 */
export async function openCall(callId: string): Promise<void> {
  if (store().call?.id === callId) {
    return;
  }
  try {
    const call = await api.calls.detail(callId);
    if (call.status !== 'ENDED') {
      applyServerCall(call);
    } else if (!isCallBusy(store())) {
      clearTimers();
      store().reset();
      store().patch({ call, phase: 'ended', endReason: call.outcome ?? 'ANSWERED' });
      closeTimer = setTimeout(() => store().reset(), ENDED_SCREEN_MS);
    }
  } catch (error) {
    if (!isCallBusy(store())) {
      fail(error);
    }
  }
}

/**
 * Relit les appels qui me concernent : au démarrage, au retour au premier
 * plan, à la reconnexion du canal. Une sonnerie arrivée pendant que
 * l'application dormait s'affiche encore si elle n'est pas finie.
 */
export async function syncActiveCalls(): Promise<void> {
  try {
    const calls = await api.calls.active();
    const current = store().call;
    if (current && isCallBusy(store()) && !calls.some((call) => call.id === current.id)) {
      // L'appel que j'affiche n'existe plus pour le serveur : il a fini sans moi.
      const fresh = await api.calls.detail(current.id).catch(() => null);
      if (fresh) {
        applyServerCall(fresh);
      }
    }
    for (const call of calls) {
      applyServerCall(call);
    }
  } catch {
    // Hors ligne : la prochaine reconnexion relira.
  }
}

// ------------------------------------------------------------- interne --

/** Quitte l'appel ici, puis le dit au serveur, qui en tire l'issue pour tous. */
async function leave(reason: CallEndReason): Promise<void> {
  const { call, phase } = store();
  if (!call || phase === null || phase === 'ended') {
    return;
  }
  finish(reason);
  try {
    await api.calls.hangUp(call.id);
  } catch {
    // Hors ligne : l'appel finira côté serveur, faute de présence.
  }
}

/** Fait sonner ici un appel entrant (ou propose de revenir dans un appel perdu). */
function adopt(call: Call): void {
  clearTimers();
  teardown();
  store().reset();
  store().patch({ call, phase: 'incoming' });
  if (myState(call) === 'RINGING') {
    ringer.ring();
    armRingTimeout(call);
  }
}

/**
 * Arrête de sonner à l'heure, même si le canal temps réel ne dit rien : la
 * fin de sonnerie est connue d'avance (`ringsUntil`), avec une marge pour
 * l'écart d'horloge entre le téléphone et le serveur.
 */
function armRingTimeout(call: Call): void {
  clearRingTimer();
  const delay = new Date(call.ringsUntil).getTime() - Date.now() + 5000;
  ringTimer = setTimeout(
    () => {
      const phase = store().phase;
      if (phase === 'outgoing') {
        void leave('MISSED');
      } else if (phase === 'incoming') {
        finish('MISSED');
      }
    },
    Math.min(Math.max(delay, 5000), 70_000),
  );
}

/** Entre dans la pièce ; `publish` : le micro (et la caméra) partent tout de suite. */
async function connect(call: Call, publish: boolean): Promise<void> {
  const stream = call.stream;
  if (!stream) {
    throw new Error(translate('calls:error.unavailable'));
  }
  await startCallAudio(call.kind);
  const next = new Room(callRoomOptions(call.kind));
  room = next;
  wire(next, call);
  await next.connect(stream.url, stream.token);
  if (room !== next) {
    // Raccroché pendant que la connexion s'établissait.
    await next.disconnect();
    return;
  }
  if (publish) {
    await publishMedia();
  }
  if (next.remoteParticipants.size > 0 && store().phase === 'connecting') {
    markActive();
  }
  notifyRoom();
}

async function publishMedia(): Promise<void> {
  const current = room;
  const { call, micOn, cameraOn, facing, speakerOn } = store();
  if (!current || !call) {
    return;
  }
  await current.localParticipant.setMicrophoneEnabled(micOn);
  if (call.kind === 'VIDEO' && cameraOn) {
    await current.localParticipant.setCameraEnabled(true, { facingMode: facing });
  }
  await routeToSpeaker(speakerOn).catch(() => undefined);
  startCallService(
    translate(call.kind === 'VIDEO' ? 'calls:service.videoTitle' : 'calls:service.audioTitle'),
    translate('calls:service.body'),
  );
  notifyRoom();
}

function markActive(): void {
  ringer.stop();
  clearRingTimer();
  store().patch({ phase: 'active', connectedAt: store().connectedAt ?? Date.now() });
}

function wire(current: Room, call: Call): void {
  const group = call.conversationType === 'GROUP';
  const changed = () => {
    if (room === current) {
      notifyRoom();
    }
  };

  current
    .on(RoomEvent.ParticipantConnected, () => {
      clearReturnTimer();
      if (store().phase === 'connecting' || store().phase === 'outgoing') {
        if (store().phase === 'outgoing') {
          void publishMedia();
        }
        markActive();
      }
      changed();
    })
    .on(RoomEvent.ParticipantDisconnected, () => {
      changed();
      // À deux, l'autre a disparu (tunnel, réseau perdu) : on lui laisse le
      // temps de revenir avant de raccrocher.
      if (!group && current.remoteParticipants.size === 0 && store().phase === 'active') {
        clearReturnTimer();
        returnTimer = setTimeout(() => void leave('ANSWERED'), REMOTE_RETURN_GRACE_MS);
      }
    })
    .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Audio) {
        detachers.push(attachRemoteAudio(track));
      }
      changed();
    })
    .on(RoomEvent.TrackUnsubscribed, changed)
    .on(RoomEvent.TrackMuted, changed)
    .on(RoomEvent.TrackUnmuted, changed)
    .on(RoomEvent.TrackStreamStateChanged, changed)
    .on(RoomEvent.LocalTrackPublished, changed)
    .on(RoomEvent.LocalTrackUnpublished, changed)
    .on(RoomEvent.ActiveSpeakersChanged, changed)
    .on(RoomEvent.Reconnecting, () => store().patch({ reconnecting: true }))
    .on(RoomEvent.Reconnected, () => store().patch({ reconnecting: false }))
    .on(
      RoomEvent.ConnectionQualityChanged,
      (quality: ConnectionQuality, participant: Participant) => {
        if (participant.isLocal) {
          store().patch({ quality: qualityOf(quality) });
        }
      },
    )
    .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      if (room !== current || reason === DisconnectReason.CLIENT_INITIATED) {
        return;
      }
      // La pièce fermée par le serveur : l'appel est fini, `call:changed`
      // dira comment. Une coupure que LiveKit n'a pas su réparer : on
      // redemande un jeton et l'on revient, une fois.
      if (reason === DisconnectReason.ROOM_DELETED) {
        finish(store().call?.outcome ?? 'ANSWERED');
        return;
      }
      void rejoin();
    });
}

async function rejoin(): Promise<void> {
  const call = store().call;
  teardown();
  if (!call) {
    return;
  }
  store().patch({ reconnecting: true });
  try {
    const joined = await api.calls.join(call.id);
    store().patch({ call: joined, reconnecting: false });
    await connect(joined, true);
  } catch (error) {
    fail(error);
  }
}

function qualityOf(quality: ConnectionQuality): CallQuality {
  if (quality === ConnectionQuality.Poor) {
    return 'poor';
  }
  if (quality === ConnectionQuality.Lost) {
    return 'lost';
  }
  return 'good';
}

/** Un geste qui n'a pas abouti : l'écran dit pourquoi, puis se ferme. */
function fail(error: unknown): void {
  const unavailable = error instanceof ApiError && error.status === 503;
  store().patch({
    error: error instanceof Error ? error.message : translate('calls:error.generic'),
  });
  finish(unavailable ? 'UNAVAILABLE' : 'FAILED');
}

/** Fin de l'appel ici : tout se libère, l'écran dit « Appel terminé », puis se vide. */
function finish(reason: CallEndReason): void {
  const wasLive = ['outgoing', 'connecting', 'active'].includes(store().phase ?? '');
  const callId = store().call?.id;
  teardown();
  if (wasLive) {
    ringer.hangup();
  }
  if (callId) {
    clearCallAlerts(callId);
  }
  store().patch({ phase: 'ended', endReason: reason, reconnecting: false });
  closeTimer = setTimeout(() => store().reset(), ENDED_SCREEN_MS);
}

/** Libère la pièce, les sons, le service de premier plan. Rejouable. */
function teardown(): void {
  clearTimers();
  ringer.stop();
  stopCallService();
  cameraPausedByBackground = false;
  for (const detach of detachers) {
    detach();
  }
  detachers = [];
  const current = room;
  room = null;
  if (current) {
    current.removeAllListeners();
    void current.disconnect();
    void stopCallAudio().catch(() => undefined);
  }
  notifyRoom();
}

function clearRingTimer(): void {
  if (ringTimer) {
    clearTimeout(ringTimer);
    ringTimer = null;
  }
}

function clearReturnTimer(): void {
  if (returnTimer) {
    clearTimeout(returnTimer);
    returnTimer = null;
  }
}

function clearTimers(): void {
  clearRingTimer();
  clearReturnTimer();
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}
